import React from 'react';
import I18n from '@/modules/i18n';
import { IQuestion, IQuestionAnswer } from '@/components/staffer/Grading/types';
import { StimuliContent, StimuliData } from '@/components/staffer/Grading/utils';
import TeacherCommentAndScore from '@/components/staffer/Grading/TeacherCommentAndScore';
import AnswerKey from '@/components/staffer/Grading/QuestionCards/AnswerKey';
import StudentAnswerInformation from '@/components/staffer/Grading/QuestionCards/StudentAnswerInformation';
import styles from '@/components/staffer/Grading/QuestionCards/questionCard.module.scss';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import { useQuestionRubric } from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import PendingStripe from '@/components/staffer/Grading/AISuggestion/PendingStripe';
import useDisplayedSuggestion from '@/components/staffer/Grading/AISuggestion/useDisplayedSuggestion';
import cn from '@/utils/cn';

type RubricData = ReturnType<typeof useQuestionRubric>['data'];

interface BaseCardProps {
  answerKeyHtml?: string;
  children: ReactChildren;
  position?: number;
  question: IQuestion;
  questionAnswer?: IQuestionAnswer;
  rubricData?: RubricData;
  sectionTitle: string;
  stimuli?: StimuliData;
  studentAnswerHtml?: ReactChildren;
}

const BaseCard = ({
  answerKeyHtml,
  children,
  position,
  question,
  questionAnswer,
  rubricData,
  sectionTitle,
  stimuli,
  studentAnswerHtml,
}: BaseCardProps) => {
  const { assignment, isTeacher } = useGradingStore();
  const { show_answers_to_students: showAnswersToStudents } = assignment || {};
  const hasPosition = typeof position === 'number';
  const questionHeader = hasPosition ? `${I18n.t('question')} ${position}` : sectionTitle;
  const displayedSuggestion = useDisplayedSuggestion(questionAnswer);
  const hasPendingSuggestion = isTeacher && displayedSuggestion?.state === 'ai_suggested_pending';

  const renderStudentAnswer = () => (
    <>
      <StudentAnswerInformation updatedAt={questionAnswer?.answer_updated_at} />
      <div className={`${styles.studentResponse} ${questionAnswer ? styles.answered : styles.unanswered}`}>
        {studentAnswerHtml}
      </div>
    </>
  );

  return (
    <div className={cn(styles.card, { 'tw-relative': hasPendingSuggestion })}>
      {hasPendingSuggestion && <PendingStripe />}
      <div className={styles.header}>
        {questionHeader}
      </div>
      <div className={styles.bodyWrapper}>
        <StimuliContent stimuli={stimuli} />
        {children}
        {renderStudentAnswer()}
        {(isTeacher || showAnswersToStudents) && (
          <AnswerKey
            answerKeyHtml={answerKeyHtml}
            question={question}
            rubricData={rubricData}
          />
        )}
        <TeacherCommentAndScore
          question={question}
          questionAnswer={questionAnswer}
        />
      </div>
    </div>
  );
};

export default BaseCard;
